import React, { useState, useEffect } from 'react';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faPencilAlt,
    faTrash,
    faEye,
    faEyeSlash,
    faCrown,
    faListUl,
    faCheck,
    faTimes,
    faCoins,
    faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import DeleteAlert from '../Shared/DeleteAlert';
import { useNavigate } from 'react-router-dom';
import ModalPopUp from '../Shared/ModalPopUp';

const AdminPlan = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, plan: null });

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await ApiServices.get('Subscription/Plan/getAll');
            if (res && res.data) {
                setPlans(res.data);
            }
        } catch (e) {
            toastHelper.error('فشل في جلب الخطط');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    const handleDelete = async () => {
        try {
            setLoading(true);
            const response = await ApiServices.delete(`Subscription/Plan/delete/${deleteDialog.plan.id}`);
            if (response && response.check) {
                toastHelper.success('تم حذف الخطة بنجاح');
                fetchPlans();
            } else {
                toastHelper.error(response.message || 'فشل في حذف الخطة');
            }
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء الحذف');
        } finally {
            setLoading(false);
            setDeleteDialog({ open: false, plan: null });
        }
    };

    const handleToggleHide = async (plan) => {
        try {
            setLoading(true);
            const res = await ApiServices.put(`Subscription/Plan/showAndHide/${plan.id}`);
            if (res && res.check) {
                toastHelper.success(`تم ${plan.is_hidden ? 'إظهار' : 'إخفاء'} الخطة بنجاح`);
                fetchPlans();
            }
        } catch (error) {
            toastHelper.error('فشل في تحديث رؤية الخطة');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            key: 'name',
            label: 'اسم الخطة',
            sortable: true,
            render: (val, row) => (
                <div className="plan-name-cell">
                    {row.is_vip && <FontAwesomeIcon icon={faCrown} className="vip-icon" title="خطة VIP" />}
                    <span className="name">{val}</span>
                    {row.is_hidden && <span className="hidden-badge">مخفية</span>}
                </div>
            )
        },
        {
            key: 'original_price',
            label: 'السعر',
            render: (val, row) => (
                <div className="price-cell">
                    {row.price_discounted && row.price_discounted !== val ? (
                        <>
                            <span className="old-price">{val} EGP</span>
                            <span className="new-price">{row.price_discounted} EGP</span>
                        </>
                    ) : (
                        <span className="price">{val} EGP</span>
                    )}
                </div>
            )
        },
        { key: 'period_name', label: 'المدة' },
        {
            key: 'max_users',
            label: 'الطلاب',
            render: (val) => <span className="stat-badge">{val || 0}</span>
        },
        {
            key: 'max_storage',
            label: 'المساحة',
            render: (val) => <span className="stat-badge">{val || 0} GB</span>
        },
        {
            key: 'features',
            label: 'المميزات',
            render: (features) => {
                const includedCount = features?.filter(f => f.included).length || 0;
                return (
                    <span className="feature-count">
                        <FontAwesomeIcon icon={faCheck} /> {includedCount} مميزات
                    </span>
                );
            }
        }
    ];

    const actions = (row) => [
        {
            label: 'تعديل',
            icon: <FontAwesomeIcon icon={faPencilAlt} />,
            onClick: (row) => navigate('/admin/plans/edit', { state: { plan: row } }),
            className: 'edit-btn'
        },
        {
            label: 'المميزات',
            icon: <FontAwesomeIcon icon={faListUl} />,
            onClick: (row) => navigate('/admin/plans/features', { state: { planId: row.id, planName: row.name } }),
            className: 'features-btn'
        },
        {
            label: row.is_hidden ? 'إظهار' : 'إخفاء',
            icon: <FontAwesomeIcon icon={row.is_hidden ? faEye : faEyeSlash} />,
            onClick: (row) => handleToggleHide(row),
            className: 'toggle-btn'
        },
        {
            label: 'حذف',
            icon: <FontAwesomeIcon icon={faTrash} />,
            onClick: (row) => setDeleteDialog({ open: true, plan: row }),
            className: 'delete-btn'
        }
    ];

    return (
        <div className="admin-plans-container">
            <div className="page-header">
                <div className="header-info">
                    <h2>خطط الاشتراك</h2>
                    <p>إدارة الباقات والأسعار والمميزات المتاحة ({plans.length})</p>
                </div>
                <button className="add-plan-btn" onClick={() => navigate('/admin/plans/add')}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>إضافة خطة جديدة</span>
                </button>
            </div>

            <SoftGrid
                columns={columns}
                data={plans}
                isLoading={loading}
                actions={actions}
                searchPlaceholder="ابحث عن خطة..."
            />

            <DeleteAlert
                isOpen={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, plan: null })}
                onConfirm={handleDelete}
                itemName={`الخطة ${deleteDialog.plan?.name}`}
                loading={loading}
            />

            <style>{`
                .admin-plans-container {
                    padding: 20px;
                    direction: rtl;
                }
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .header-info h2 {
                    margin: 0;
                    color: #1e293b;
                    font-size: 1.5rem;
                    font-weight: 800;
                }
                .header-info p {
                    margin: 4px 0 0;
                    color: #64748b;
                }
                .add-plan-btn {
                    background: #6366f1;
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.4);
                }
                .add-plan-btn:hover {
                    background: #4f46e5;
                    transform: translateY(-1px);
                }
                
                .plan-name-cell {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .vip-icon {
                    color: #6366f1;
                    font-size: 1.1rem;
                }
                .hidden-badge {
                    background: #fee2e2;
                    color: #ef4444;
                    font-size: 0.75rem;
                    padding: 2px 8px;
                    border-radius: 6px;
                    font-weight: 700;
                }
                
                .price-cell {
                    display: flex;
                    flex-direction: column;
                }
                .old-price {
                    text-decoration: line-through;
                    color: #94a3b8;
                    font-size: 0.85rem;
                }
                .new-price, .price {
                    color: #10b981;
                    font-weight: 800;
                }

                .stat-badge {
                    background: #f1f5f9;
                    color: #475569;
                    padding: 4px 10px;
                    border-radius: 8px;
                    font-weight: 700;
                    font-size: 0.9rem;
                }
                
                .feature-count {
                    color: #6366f1;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                /* Modern action button colors */
                .features-btn { color: #8b5cf6 !important; }
                .toggle-btn { color: #6366f1 !important; }
            `}</style>
        </div>
    );
};

export default AdminPlan;
